// Tour viewer entry point. Loads the tour manifest from the API, shows the poster at once, streams
// the splat coarse-to-fine while the room assembles itself from the floor up, and enables walking as soon as the collision voxels
// (a few hundred KB) have arrived.
import {
    AppBase, AppOptions, CameraComponentSystem, Color, DEVICETYPE_WEBGL2, DEVICETYPE_WEBGPU, Entity,
    FILLMODE_FILL_WINDOW, GSplatComponentSystem, GSplatHandler, RESOLUTION_AUTO, ScriptComponentSystem,
    ScriptHandler, TONEMAP_LINEAR, TextureHandler, createGraphicsDevice, platform, Texture, EnvLighting, Quat,
    PIXELFORMAT_RGBA8, TEXTUREPROJECTION_EQUIRECT, ADDRESS_REPEAT, ADDRESS_CLAMP_TO_EDGE, XrManager
} from 'playcanvas';
import { GSplatCropShaderEffect } from 'playcanvas/scripts/esm/gsplat/shader-effect-crop.mjs';

import { Character } from './collision/character.js';
import { buildNavGrid } from './collision/navgrid.js';
import { VoxelGrid } from './collision/voxel-grid.js';
import { Input } from './input.js';
import { DroneTour, OverviewMode, WalkMode, screenRay } from './modes.js';
import { applyBranding } from './branding.js';
import { loadProgressive, setSplatBudget } from './progressive.js';
import { Analytics } from './analytics.js';
import { installCpuCenters } from './centers-cpu.js';
import { FrameGovernor } from './frame-governor.js';
import { computeArrivalField } from './effects/arrival-field.js';
import { GSplatRevealBuild } from './effects/reveal-build.js';
import { Theme } from './theme.js';
import { UI } from './ui.js';
import { VrMode } from './xr.js';

const root = document.getElementById('tour');
const REVEAL_TINT_LIGHT = new Color(0.25, 0.22, 0.18);
const REVEAL_TINT_DARK = new Color(0.12, 0.13, 0.16);
// Canvas background, matching --bg in style.css
const CLEAR = { light: new Color(0.925, 0.933, 0.945), dark: new Color(0.043, 0.047, 0.059) };
const params = new URLSearchParams(location.search);
const tourId = root.dataset.tour || params.get('tour');
const apiBase = root.dataset.api || '';

const BUDGET_CAP = { desktop: 1500000, mobile: 1000000, low: 500000 };
const WALK_MASK_RADIUS = 0.2;   // m, body radius on the build's walk mask (no collision data)

// Field of view (vertical, degrees): wide from the start, so a visitor sees the room and not a slice of it.
// Landscape screens get WIDE; upright phones keep at least ACROSS degrees across (60° vertical left a phone held
// upright a 30° slit), up to MAX vertical. Pinch and wheel zoom scale this (WalkMode.zoom).
const FOV = { wide: 75, across: 66, max: 108 };
function fitFov(aspect) {
    const across = 2 * Math.atan(Math.tan(FOV.across * Math.PI / 360) / aspect) * 180 / Math.PI;
    return Math.min(FOV.max, Math.max(FOV.wide, across));
}

/** The manifest profile for this device; `?quality=desktop|mobile|low` forces one unless `forced` is false */
function deviceProfile(lod, forced = true) {
    const want = forced && params.get('quality');
    if (want && lod.profiles[want]) return want;
    const mem = navigator.deviceMemory || 8;
    const cores = navigator.hardwareConcurrency || 8;
    if (platform.mobile) return mem <= 3 || cores <= 4 ? 'low' : 'mobile';
    return mem <= 4 ? 'mobile' : 'desktop';
}

async function main() {
    const theme = new Theme(params.get('theme') || 'auto');
    const ui = new UI(root);
    if (!tourId) return ui.fatal('Kein Rundgang angegeben.');
    ui.loading(true);

    // Graphics device: WebGPU where available, WebGL2 otherwise (?gfx=webgl2 forces it). Started right away, so
    // the GPU setup runs while the manifest is on its way.
    const canvas = document.createElement('canvas');
    canvas.className = 'tour-canvas';
    const types = params.get('gfx') === 'webgl2' ? [DEVICETYPE_WEBGL2] : [DEVICETYPE_WEBGPU, DEVICETYPE_WEBGL2];
    // VR headsets: WebXR draws through WebGL2 unless the browser can hand it WebGPU frames (XRGPUBinding). The
    // question takes a few ms where WebXR exists and none elsewhere; it is not waited on longer than 300 ms.
    const vrCapable = navigator.xr && !globalThis.XRGPUBinding
        ? Promise.race([navigator.xr.isSessionSupported('immersive-vr').catch(() => false), new Promise(r => setTimeout(r, 300, false))])
        : Promise.resolve(false);
    const deviceReady = vrCapable.then(vr => createGraphicsDevice(canvas, { deviceTypes: vr ? [DEVICETYPE_WEBGL2] : types, antialias: false }));
    deviceReady.catch(() => {});

    // Default credentials mode, so the embed page's preload of this URL is reused
    const res = await fetch(`${apiBase}/v1/tours/${encodeURIComponent(tourId)}`);
    if (!res.ok) {
        deviceReady.then(d => d.destroy(), () => {});
        return ui.fatal(res.status === 404 ? 'Dieser Rundgang ist nicht verfügbar.' : 'Rundgang konnte nicht geladen werden.');
    }
    const tour = await res.json();
    const m = tour.manifest;
    const asset = p => (p ? new URL(p, tour.assetBase).toString() : null);
    ui.setTitle(tour.title || m.title);
    if (params.get('title') === '0') ui.hideTitle();
    // Enterprise branding: colours, radius, font, logo; the canvas background follows it too
    const brand = applyBranding(tour.branding, ui);
    // Standard tours carry a small "3D-Rundgang von Horama" in the menu; a brand of their own only a quiet "Technik: Horama"
    ui.setCredit(brand.branded);
    if (brand.clear.light) CLEAR.light = brand.clear.light;
    if (brand.clear.dark) CLEAR.dark = brand.clear.dark;
    if (m.assets.poster) ui.showPoster(asset(m.assets.poster));

    const analytics = new Analytics(apiBase, tourId, tour.viewToken);
    analytics.event('view', { profile: deviceProfile(m.lod) });

    root.prepend(canvas);
    let device;
    try {
        device = await deviceReady;
    } catch (e) {
        analytics.event('error', { stage: 'device', message: String(e) });
        return ui.fatal('Ihr Browser unterstützt keine 3D-Darstellung.');
    }
    // Software rendering (GPU acceleration off or blocklisted): low RAM use, heavy stutter. Detect
    // it, drop to the lightest profile and tell the visitor why it is slow.
    const gpuName = String(device.unmaskedRenderer || device.gpuAdapter?.info?.description || device.gpuAdapter?.info?.architecture || '');
    const software = /swiftshader|llvmpipe|softpipe|software|basic render/i.test(gpuName) || device.gpuAdapter?.info?.isFallbackAdapter;
    if (software) {
        analytics.event('error', { stage: 'software-rendering', message: gpuName.slice(0, 80) });
        ui.notice('Grafikbeschleunigung ist aus – der Rundgang ruckelt. Bitte in Chrome, Safari oder Edge mit Hardwarebeschleunigung öffnen.', 'alert');
    }
    const profileName = software ? 'low' : deviceProfile(m.lod);
    // What Auto picks (the device, or less after frames kept dropping); ?quality= is a choice like the menu's
    const autoBase = software ? 'low' : deviceProfile(m.lod, false);
    // Splats drawn per frame, whatever the manifest allows: the engine fills the budget by distance (fine
    // near the camera, coarse far away), so what is close stays sharp. Measured on an Apple M3 (Retina): all
    // 3 M splats of a flat walked at about 20 fps. ?budget= overrides it for measurements.
    const profile = { ...m.lod.profiles[profileName] };
    profile.budget = Math.min(profile.budget, BUDGET_CAP[profileName] ?? BUDGET_CAP.low);
    if (+params.get('budget')) profile.budget = +params.get('budget');
    // Pixel ratio: splats are fill-rate bound, so start below full Retina and let the governor adapt
    const dpr = window.devicePixelRatio || 1;
    const ratioFor = (name) => {
        const fixed = parseFloat(params.get('ratio'));
        if (fixed) return { start: fixed, cap: fixed, min: fixed };
        return name === 'desktop'
            ? { cap: Math.min(dpr, 2), start: Math.min(dpr, 1.5), min: Math.min(dpr, 1) }
            : { cap: Math.min(dpr, 1.5), start: Math.min(dpr, name === 'low' ? 1 : 1.25), min: 0.75 };
    };
    const ratioLimits = ratioFor(profileName);
    device.maxPixelRatio = ratioLimits.start;
    // The lightest profile and software rendering get solid surfaces: blur behind glass is redrawn with every frame
    ui.setLite(profileName === 'low');

    const opts = new AppOptions();
    opts.graphicsDevice = device;
    opts.componentSystems = [CameraComponentSystem, GSplatComponentSystem, ScriptComponentSystem];
    // TextureHandler is required: streamed SOG chunks are WebP textures
    opts.resourceHandlers = [GSplatHandler, TextureHandler, ScriptHandler];
    opts.xr = XrManager;
    const app = new AppBase(canvas);
    app.init(opts);
    // WebGPU sorts the splats on the GPU (the engine's default renderer there): the CPU copy of every chunk's
    // centres (a GPU readback and a loop over all splats per chunk, 12 bytes per splat) would go unused.
    // WebGL2 sorts on the CPU and keeps them.
    if (device.isWebGPU) app.scene.gsplatCentersEnabled = false;
    // Engine settings from the performance research (docs/performance/bericht.md, 31.10.2026); ?engine=default keeps
    // the engine's own values for A/B measurements
    if (params.get('engine') !== 'default') {
        const gs = app.scene.gsplat;
        gs.cooldownTicks = 1e9;         // keep loaded LOD files: after 100 idle frames the engine dropped them and reloaded each ~5x per 90 s
        gs.lodBehindPenalty = 1;        // turning around changes no LOD (1.5 loaded coarser behind the camera)
        gs.colorUpdateAngle = 30;       // view-dependent colour refreshed every 30° of view change instead of 10°
        if (!device.isWebGPU) installCpuCenters();   // WebGL2: chunk centres decoded in a CPU worker, no GPU readback per file
    }
    app.setCanvasFillMode(FILLMODE_FILL_WINDOW);
    app.setCanvasResolution(RESOLUTION_AUTO);
    const onResize = () => app.resizeCanvas();
    window.addEventListener('resize', onResize);
    app.start();

    const camera = new Entity('camera');
    camera.addComponent('camera', {
        clearColor: CLEAR[theme.value],
        fov: fitFov(innerWidth / Math.max(1, innerHeight)),
        nearClip: 0.05,
        farClip: 200,
        toneMapping: TONEMAP_LINEAR
    });
    // The camera hangs under a rig: outside VR the rig stays at the origin, in VR the headset moves the camera
    // inside it and walking moves the rig
    const rig = new Entity('rig');
    rig.addChild(camera);
    app.root.addChild(rig);
    // Resolution follows the GPU budget inside a fixed render target (no canvas resizes; frames at rest at the cap
    // ratio); ?scale=off keeps the old canvas-resize steps for A/B measurements
    const governor = new FrameGovernor(app, camera, ratioLimits, { scale: params.get('scale') !== 'off' });
    governor.hold('loading', true);
    theme.onChange((t) => { camera.camera.clearColor = CLEAR[t]; governor.wake(); });
    if (params.has('debug')) {
        window.__governor = governor;
        window.__ui = ui;
        const ua = navigator.userAgent;
        const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
        const gpu = String(device.unmaskedRenderer || device.gpuAdapter?.info?.description || device.gpuAdapter?.info?.vendor || '').slice(0, 40);
        ui.debugOverlay(() => `${browser} · ${device.deviceType} · ${gpu || 'GPU ?'}\n${governor.fps ?? '–'} fps · Spitzen ${governor.p90 ?? '–'} ms · ` +
            `Pixel ${governor.ratio} · ${device.width}×${device.height} · ${(app.stats.frame.gsplats / 1000).toFixed(0)}k Splats`);
    }
    const [sx, sy, sz] = m.start.position;
    camera.setPosition(sx, sy, sz);
    camera.setEulerAngles(m.start.pitch, m.start.yaw, 0);

    // --- splat: coarse first, finer levels in the background ---
    let revealDone = !m.intro || m.intro.effect === 'none';
    // ?light=<variant id or label> starts in a computed time of day instead of the capture (stills, previews)
    const want = params.get('light');
    const startLight = want && m.lighting?.variants?.find(v => v.id === want || v.label.toLowerCase() === want.toLowerCase());

    // --- the outside: a panorama behind the splats, seen through the windows (manifest assets.skybox; one per time of
    // day in `skies`, the capture showing `captureSky`). Loaded beside the splat, it appears when it has arrived. ---
    const skyOf = (o) => o?.sky ?? (m.skies?.[o?.label?.toLowerCase()] ? o.label.toLowerCase() : null);
    let skyReq = 0;
    async function setSky(name) {
        const set = (name && m.skies?.[name]) || { skybox: m.assets.skybox, skyboxMobile: m.assets.skyboxMobile };
        const url = asset(profileName === 'desktop' ? set.skybox : (set.skyboxMobile || set.skybox));
        if (!url) return;
        const req = ++skyReq;
        try {
            // As an ImageBitmap: WebGPU takes no <img> as texture source
            const img = await createImageBitmap(await (await fetch(url)).blob());
            if (req !== skyReq) return;   // another time was picked meanwhile
            const tex = new Texture(device, { name: 'sky', width: img.width, height: img.height, format: PIXELFORMAT_RGBA8, mipmaps: false,
                projection: TEXTUREPROJECTION_EQUIRECT, addressU: ADDRESS_REPEAT, addressV: ADDRESS_CLAMP_TO_EDGE });
            tex.setSource(img);
            const old = app.scene.skybox;
            app.scene.skybox = EnvLighting.generateSkyboxCubemap(tex, profileName === 'desktop' ? 1024 : 512);
            app.scene.skyboxRotation = new Quat().setFromEulerAngles(0, m.skyboxYaw || 0, 0);
            tex.destroy();
            old?.destroy();
            governor.wake();
        } catch (e) {
            analytics.event('error', { stage: 'skybox', message: String(e) });
        }
    }
    // ?sky=<morgen|mittag|abend|nacht> shows another panorama (previews)
    setSky(params.get('sky') || skyOf(startLight) || m.captureSky);
    // Work that may wait until the splat is complete (it would compete with the chunks for the main thread)
    let onLoaded = [], loaded = () => { if (!onLoaded) return; for (const f of onLoaded) f(); onLoaded = null; };
    let splatLoaded = false;
    const whenLoaded = (f) => (onLoaded ? onLoaded.push(f) : f());
    // The poster stays until the first frame is drawn and the intro is set up behind it
    let firstFrameSeen = false, introReady = false;
    const maybeHidePoster = () => { if (firstFrameSeen && introReady) ui.hidePoster(revealDone ? 0 : 120); };
    const splat = loadProgressive(app, {
        url: asset(startLight?.lod ?? m.assets.lod),
        lod: m.lod,
        profile,
        concurrency: profileName === 'desktop' ? 6 : 3,
        onFirstFrame: () => {
            firstFrameSeen = true;
            ui.loading(false);
            maybeHidePoster();
            if (params.has('debug')) window.__firstFrame = Math.round(performance.now());
            analytics.event('firstFrame', { ms: Math.round(performance.now()) });
        },
        onLevel: ({ level, finest, levels }) => {
            ui.setProgress(levels - level, levels - finest, level === finest);
            if (level === finest) { governor.hold('loading', false); loaded(); }
            if (params.has('debug')) (window.__levels ??= []).push({ level, ms: Math.round(performance.now()) });
        }
    }).catch(e => {
        analytics.event('error', { stage: 'splat', message: String(e) });
        governor.hold('loading', false);
        ui.fatal('Die 3D-Daten konnten nicht geladen werden.');
        throw e;
    });

    // --- collision: walking needs it, everything else works without ---
    const collision = m.assets.collision
        ? VoxelGrid.load(asset(m.assets.collision)).catch(e => {
            analytics.event('error', { stage: 'collision', message: String(e) });
            return null;
        })
        : Promise.resolve(null);

    // Reassigned when the visitor switches the time of day (another LOD set, same geometry)
    let { entity, asset: splatAsset } = await splat;
    if (params.has('debug')) window.__tour = { app, camera, entity, manifest: m };
    // Walking is always the starting mode. Without collision data the visitor still walks at eye
    // height on a flat floor inside the floor-plan bounds, just without walls.
    const collisionGrid = await collision;
    // Without collision the build's walk mask (rooms, furniture and walls in the body band) walls the flat floor in:
    // the worst frames of the 31.10. measurements came from eyes inside furniture or behind walls
    const walkMask = !collisionGrid && m.assets.walkFallback
        ? await VoxelGrid.loadWalkMask(asset(m.assets.walkFallback)).catch(e => {
            analytics.event('error', { stage: 'walk-mask', message: String(e) });
            return null;
        })
        : null;
    const grid = collisionGrid ?? VoxelGrid.flatFloor(m.bounds ?? { min: [sx - 5, 0, sz - 5], max: [sx + 5, 0, sz + 5] }, 0.05, walkMask);
    if (!revealDone) {
        entity.addComponent('script');
        const reveal = entity.script.create(GSplatRevealBuild);
        // When the build-up front reaches each surface: shortest path along the surfaces, starting at
        // the surfaces nearest the visitor above, below and to the sides
        reveal.setField(computeArrivalField(grid, [sx, sy, sz]));
        reveal.tint.copy(theme.value === 'dark' ? REVEAL_TINT_DARK : REVEAL_TINT_LIGHT);
        governor.hold('intro', true);
        setTimeout(() => { revealDone = true; governor.hold('intro', false); }, reveal.getCompletionTime() * 1000 + 100);
    }
    introReady = true;
    maybeHidePoster();

    const input = new Input(canvas);
    // Desktop free look: crosshair while active, a one-time hint how to leave it
    let lookHintShown = false;
    input.onLookChange = (active) => {
        root.classList.toggle('looking', active);
        governor.wake();
        if (active && !lookHintShown) {
            lookHintShown = true;
            ui.notice('Maus bewegen zum Umsehen. Esc oder ein Klick gibt die Maus wieder frei.', 'esc');
        }
    };
    const touch = platform.touch;
    let crop = null;
    // On the walk mask the eye keeps 20 cm from walls and furniture (Jann, 31.10.2026); real collision keeps its 0.18 m
    const character = new Character(grid, walkMask ? { ...m.walk, radius: WALK_MASK_RADIUS } : m.walk);
    if (!character.spawn(sx, sy - m.walk.eyeHeight, sz, 1.5)) {
        analytics.event('error', { stage: 'spawn' });
    }
    const walk = new WalkMode(camera, character, m.walk);
    walk.baseFov = camera.camera.fov;
    let drone = null;   // the running drone tour (see below)
    // Turning the phone changes the aspect: keep the view wide, with the visitor's zoom on top
    window.addEventListener('resize', () => {
        walk.baseFov = fitFov(canvas.clientWidth / Math.max(1, canvas.clientHeight));
        if (drone) drone.baseFov = walk.baseFov;   // the drone sets its own width, eased
        else walk.applyFov();
        governor.wake();
    });
    walk.setView(m.start.yaw, m.start.pitch);
    // The pipeline puts the start view at walking eye height; if the floor found here differs a little
    // (older tours, spawn moved out of geometry), glide into place instead of jumping
    walk.blendFrom(camera.getPosition().toArray(), 0.8);
    input.onUserInput = () => walk.cancelPath();
    const overview = new OverviewMode(camera, m.bounds ?? grid);
    let mode = 'walk';
    // Navigation grid for tap-to-walk and room jumps; built when the browser is idle once the splat is complete
    // (a tap or room jump before that builds it at once)
    const buildNav = () => { if (!walk.nav) walk.nav = buildNavGrid(character); };
    whenLoaded(() => { splatLoaded = true; (window.requestIdleCallback || setTimeout)(buildNav, { timeout: 1500 }); });
    walk.ensureNav = buildNav;

    const setMode = (next) => {
        if (next === mode) return;
        mode = next;
        input.sticksEnabled = mode !== 'overview';
        governor.wake();
        ui.setMode(mode);
        analytics.event('mode', { mode });
        if (mode === 'overview') {
            addCrop();
        } else {
            if (crop) entity.script.destroy(GSplatCropShaderEffect.scriptName);
            crop = null;
        }
    };

    // Cut the ceiling away so rooms are visible from above (dollhouse view)
    function addCrop() {
        if (!entity.script) entity.addComponent('script');
        entity.script.destroy(GSplatRevealBuild.scriptName);
        crop = entity.script.create(GSplatCropShaderEffect);
        const cut = Math.max(1.2, (character?.headroom(3) ?? 2.4) - 0.25);
        crop.aabbMin.set(-1e4, -1e4, -1e4);
        crop.aabbMax.set(1e4, cut, 1e4);
    }

    // --- time of day: computed sunlight as extra LOD sets (manifest.lighting), shown as a simulation ---
    if (m.lighting?.variants?.length) {
        const options = [{ id: 'capture', label: m.lighting.captureLabel || 'Aufnahme', lod: m.assets.lod },
            ...m.lighting.variants];
        let current = startLight?.id ?? 'capture';
        let pending = null;
        ui.setLighting(options, m.lighting.note, async (id) => {
            if (id === current || id === pending) return;
            pending = id;
            ui.setCurrentLighting(current, id);
            governor.wake();
            governor.hold('lighting', true);
            analytics.event('lighting', { id });
            let sharp;
            const arrived = new Promise((r) => { sharp = r; });
            try {
                // The new set streams in over the old one; the old one goes once the new one is sharp
                const next = await loadProgressive(app, {
                    url: asset(options.find(o => o.id === id).lod),
                    lod: m.lod,
                    profile,
                    concurrency: profileName === 'desktop' ? 6 : 3,
                    onLevel: ({ level, finest }) => {
                        // Once the new set is nearly sharp, drop the old one to its coarsest level:
                        // rendering two full sets at once was what made switching stutter
                        if (level <= finest + 1 && entity.gsplat) {
                            const worst = (entity.gsplat.resource?.octree?.lodLevels ?? m.lod.levels) - 1;
                            entity.gsplat.lodRangeMin = entity.gsplat.lodRangeMax = worst;
                        }
                        if (level === finest) sharp();
                    }
                });
                await arrived;
                if (pending !== id) {   // the visitor picked another time meanwhile
                    next.entity.destroy();
                    app.assets.remove(next.asset);
                    next.asset.unload();
                    return;
                }
                const old = { entity, asset: splatAsset };
                entity = next.entity;
                splatAsset = next.asset;
                if (crop) addCrop();
                old.entity.destroy();
                app.assets.remove(old.asset);
                old.asset.unload();
                current = id;
                pending = null;
                setSky(skyOf(options.find(o => o.id === id)) || m.captureSky);
                if (params.has('debug')) window.__tour.entity = entity;
            } catch (e) {
                analytics.event('error', { stage: 'lighting', message: String(e) });
                if (pending === id) pending = null;
                ui.notice('Diese Tageszeit konnte nicht geladen werden.');
            } finally {
                ui.setCurrentLighting(current, pending);
                if (!pending) governor.hold('lighting', false);
            }
        });
        ui.setCurrentLighting(current);
    }

    ui.onMode = () => {
        if (mode === 'tour') { stopTour(); return; }
        setMode(mode === 'walk' ? 'overview' : 'walk');
    };
    ui.setMode(mode);
    ui.setRooms(m.rooms, (room) => {
        setMode('walk');
        goToRoom(room);
    });

    function walkTo(target, opts) {
        walk.ensureNav();
        const nav = walk.nav;
        const from = nav.nearest(...character.pos, 1.0);
        const to = nav.nearest(target[0], target[1], target[2], 1.0);
        const ids = nav.path(from, to);
        if (!ids) return false;
        walk.follow(nav.smooth(ids), opts);
        return true;
    }

    function goToRoom(room) {
        analytics.event('room', { room: room.id });
        if (mode === 'tour') stopTour();
        const [x, y, z] = room.position;
        const feet = [x, y - m.walk.eyeHeight, z];
        const pitch = room.pitch ?? -3;
        // Walk there: look ahead along the path, level the view, brake softly, then turn into the
        // room's view. Far away (other storey, no path): fade and arrive already facing the room.
        const near = Math.hypot(x - character.pos[0], z - character.pos[2]) < 10;
        if (near && walkTo(feet, { yaw: room.yaw, pitch })) return;
        ui.fade(() => {
            if (character.teleport(...feet)) {
                walk.setView(room.yaw, pitch);
                walk.eyeY = null;
                walk.blend = null;
                walk.cancelPath();
            }
        });
    }

    // --- drone tour: automatic flight through all rooms; only with real collision data (on the flat
    // stand-in floor it would fly through walls) ---
    const canTour = !!collisionGrid;
    let tourClicks = 0;
    // Points of interest set by the agent for objects in the flat (tour settings or manifest)
    const pois = tour.pois ?? m.pois ?? [];
    const tourStops = () => {
        walk.ensureNav();
        if (m.rooms?.length >= 2) return m.rooms;
        return autoStops(walk.nav, character, m);
    };
    function startTour() {
        if (!canTour) return;
        walk.ensureNav();
        const stops = tourStops();
        if (stops.length < 1) return;
        if (mode === 'overview') setMode('walk');
        drone = new DroneTour(camera, character, walk.nav, stops, {
            ...m.tour,
            pois,
            baseFov: walk.baseFov,
            onStop: (stop) => { if (stop.id) { ui.setCurrentRoom(stop.id); analytics.room(stop.id); } }
        });
        if (!drone.start()) { drone = null; ui.notice('Für diese Wohnung ist keine Tour verfügbar.'); return; }
        walk.cancelPath();
        mode = 'tour';
        governor.hold('tour', true);
        // A click during the tour only ends the tour; free look starts with the next one
        input.stopLook();
        input.lookEnabled = false;
        tourClicks = input.clicks;
        ui.setTour(true, true);
        analytics.event('tour', { action: 'start', stops: stops.length });
    }
    function stopTour() {
        if (!drone) return;
        const feet = drone.landing(), view = drone.view;
        drone = null;
        governor.hold('tour', false);
        input.lookEnabled = true;
        ui.setTour(true, false);
        // Land where the drone is: stand on the floor below, keep the view, glide up to eye height
        const p = camera.getPosition();
        character.teleport(...feet);
        walk.setView(view.yaw, Math.max(-30, Math.min(20, view.pitch)));
        walk.blendFrom([p.x, p.y, p.z], 0.9);
        mode = 'walk';
        ui.setMode(mode);
        analytics.event('tour', { action: 'stop' });
    }
    ui.setTour(canTour, false);
    ui.onTour = () => (drone ? stopTour() : startTour());
    if (canTour && (params.get('autotour') === '1' || m.tour?.autoplay)) {
        setTimeout(() => { if (mode === 'walk' && !input.busy) startTour(); }, revealDone ? 500 : 4500);
    }

    // --- motion look: turning the phone or tablet turns the view (a switch in the menu; iOS asks for the sensor on
    // the tap). The first time a card shows how, later a short message. ---
    ui.setGyro(touch && 'DeviceOrientationEvent' in window);
    let gyroShown = false;
    ui.onGyro = async () => {
        if (input.gyroOn) {
            input.stopGyro();
            ui.setGyro(true, false);
        } else if (await input.startGyro()) {
            ui.setGyro(true, true);
            if (gyroShown) ui.notice('Handy bewegen zum Umsehen.', 'gyro');
            else ui.showGuide('gyro', { actions: [{ label: 'Verstanden', primary: true }], untilUse: true, timeout: 7000 });
            gyroShown = true;
        } else {
            ui.notice('Bewegungssensor nicht verfügbar oder nicht erlaubt.', 'alert');
            return;
        }
        analytics.event('gyro', { on: input.gyroOn });
        governor.wake();
    };

    // --- VR headsets (WebXR): the button appears when the browser reports a headset ---
    const vr = new VrMode(app, rig, camera, character, m.walk, () => { walk.ensureNav(); return walk.nav; });
    vr.watch(ok => ui.setVr(ok));
    ui.onVr = async () => {
        if (vr.active) return;
        if (mode === 'tour') stopTour();
        if (mode === 'overview') setMode('walk');
        input.stopLook();
        walk.cancelPath();
        if (!(await vr.start(walk.yaw.x))) ui.notice('VR konnte nicht gestartet werden.', 'alert');
    };
    vr.onStart = () => {
        governor.hold('xr', true);
        analytics.event('vr', { action: 'start' });
    };
    vr.onEnd = (yaw) => {
        // Back on the screen where the visitor stood, looking where they looked
        walk.setView(yaw, 0);
        walk.eyeY = null;
        walk.blend = null;
        walk.applyFov();
        app.resizeCanvas();
        governor.hold('xr', false);
        analytics.event('vr', { action: 'stop' });
    };

    // --- picture quality (menu): Auto follows the device and steps down while frames keep dropping; Hoch, Mittel and
    // Niedrig are the manifest's desktop, mobile and low profiles (splat budget, finest level, pixel ratio). Not
    // stored: every visit starts on Auto (or ?quality=). ---
    const QUALITY = [['auto', 'Auto'], ['desktop', 'Hoch'], ['mobile', 'Mittel'], ['low', 'Niedrig']]
        .filter(([id]) => id === 'auto' || m.lod.profiles[id]);
    const QUALITY_NOTE = { desktop: 'Am schärfsten, für kräftige Geräte', mobile: 'Ausgewogen', low: 'Für ältere Geräte, spart Daten' };
    const forced = params.get('quality');
    let quality = forced && m.lod.profiles[forced] ? forced : 'auto';
    let autoProfile = autoBase, active = profileName;
    const qualityLabel = (id) => QUALITY.find(q => q[0] === id)?.[1] ?? id;
    const qualityNote = () => (quality === 'auto' ? `Passt sich dem Gerät an, jetzt: ${qualityLabel(active)}` : QUALITY_NOTE[quality]);
    /** Draw with profile `name` from now on; `ratioBelow` keeps the pixel ratio from rising (stepping down) */
    function useProfile(name, ratioBelow = Infinity) {
        active = name;
        const p = m.lod.profiles[name];
        profile.budget = +params.get('budget') || Math.min(p.budget, BUDGET_CAP[name] ?? BUDGET_CAP.low);
        profile.finest = p.finest;
        setSplatBudget(app, splatAsset, profile.budget);
        const gs = entity.gsplat;
        if (gs) gs.lodRangeMin = Math.min(Math.max(profile.finest ?? 0, 0), (gs.resource?.octree?.lodLevels ?? m.lod.levels) - 1);
        governor.setLimits(ratioFor(name), ratioBelow);
        ui.setLite(software || name === 'low');
    }
    ui.setQuality(QUALITY.map(([id, label]) => ({ id, label })), quality, qualityNote(), (id) => {
        if (id === quality) return;
        quality = id;
        useProfile(id === 'auto' ? autoProfile : id);
        ui.setCurrentQuality(quality, qualityNote());
        analytics.event('quality', { quality: id, profile: active });
    });
    // Auto on a weak device: frames keep dropping at the lowest pixel ratio, so the next lighter profile (once the
    // splat is complete, never back up)
    const LIGHTER = ['desktop', 'mobile', 'low'].filter(n => m.lod.profiles[n]);
    governor.onStarved = () => {
        const next = LIGHTER[LIGHTER.indexOf(active) + 1];
        if (quality !== 'auto' || !splatLoaded || vr.active || !next) return;
        autoProfile = next;
        useProfile(next, governor.ratio);
        ui.setCurrentQuality(quality, qualityNote());
        ui.notice('Bildqualität angepasst, damit der Rundgang flüssig läuft.', 'quality');
        analytics.event('quality', { quality: 'auto', profile: next, starved: 1 });
    };

    let roomTimer = 0;
    app.on('update', (dt) => {
        dt = Math.min(dt, 0.1);
        if (vr.active) {
            input.takeTaps(); input.takeLook(); input.takeMouse(); input.takeZoom(); input.takeGyro();
            vr.update(dt);
            roomTimer += dt;
            if (roomTimer > 1) {
                roomTimer = 0;
                const room = nearestRoom(m.rooms, character.pos);
                if (room) analytics.room(room.id);
            }
            return;
        }
        if (mode === 'tour') {
            // Any input hands control back to the visitor
            const taps = input.takeTaps();
            input.takeGyro();   // the drone holds the camera; turning the phone does not end the tour
            if (input.busy || taps.length || input.takeZoom() || input.clicks !== tourClicks) {
                input.takeLook();
                stopTour();
            } else if (!drone.update(dt)) {
                stopTour();
            }
            roomTimer += dt;
            if (roomTimer > 0.5) {
                roomTimer = 0;
                const p = camera.getPosition();
                const room = nearestRoom(m.rooms, [p.x, p.y, p.z]);
                ui.setCurrentRoom(room?.id);
                if (room) analytics.room(room.id);
            }
            return;
        }
        for (const tap of input.takeTaps()) handleTap(tap);
        if (mode === 'walk') walk.update(dt, input, touch);
        else overview.update(dt, input);
        roomTimer += dt;
        if (roomTimer > 1) {
            roomTimer = 0;
            const room = nearestRoom(m.rooms, character.pos);
            ui.setCurrentRoom(room?.id);
            if (room) analytics.room(room.id);
        }
    });

    function handleTap({ x, y }) {
        const ray = screenRay(camera, x, y);
        const hit = grid.raycast(ray.origin, ray.dir, 40);
        if (!hit) return;
        let target = hit.point;
        if (hit.normal[1] !== 1) {
            // Tapped a wall or furniture: aim for the floor in front of it
            target = [hit.point[0] - ray.dir[0] * 0.35, hit.point[1], hit.point[2] - ray.dir[2] * 0.35];
        }
        if (mode === 'overview') {
            // Tap into the dollhouse: jump into walk mode there
            setMode('walk');
            ui.fade(() => {
                if (character.teleport(target[0], target[1] + 0.3, target[2])) {
                    walk.eyeY = null;
                    walk.cancelPath();
                }
            });
            return;
        }
        if (walkTo(target)) ui.tapMarker(x, y);
        else ui.tapMarker(x, y, true);
    }

    // Debug helper for placing points of interest: look at an object (free look, crosshair) and press P
    if (params.has('debug')) {
        const placed = [...pois];
        window.addEventListener('keydown', (e) => {
            if (e.key.toLowerCase() !== 'p') return;
            const ray = screenRay(camera, canvas.clientWidth / 2, canvas.clientHeight / 2);
            const hit = grid.raycast(ray.origin, ray.dir, 30);
            if (!hit) return ui.notice('Kein Objekt in der Bildmitte getroffen.');
            const n = placed.length + 1;
            placed.push({ id: `poi-${n}`, name: `Punkt ${n}`, position: hit.point.map(v => +v.toFixed(3)), radius: 2.2, weight: 1 });
            pois.splice(0, pois.length, ...placed);
            const json = JSON.stringify(placed, null, 1);
            console.log('Points of interest (tour.json "pois"):\n' + json);
            navigator.clipboard?.writeText(json).catch(() => {});
            ui.notice(`Punkt ${n} gesetzt (${placed.length} insgesamt) – JSON in Zwischenablage und Konsole.`);
        });
    }

    if (touch) input.showSticks();
    // ?guide=0: no guide card (stills, presentations)
    ui.ready(touch, { guide: params.get('guide') !== '0' });
    analytics.start();
}

/**
 * Tour stops when no rooms are annotated: spread points over the walkable area (farthest-point
 * sampling from the start), each looking towards the middle of the floor plan.
 */
function autoStops(nav, character, m) {
    const reach = nav.reachable(nav.nearest(...character.pos, 1.5));
    const ids = [];
    reach.forEach((v, id) => v && ids.push(id));
    if (!ids.length) return [];
    const picked = [nav.nodes[nav.nearest(...character.pos, 1.5)]];
    for (let k = 0; k < 4; k++) {
        let best = null, bd = 0;
        for (let i = 0; i < ids.length; i += 3) {
            const n = nav.nodes[ids[i]];
            const d = Math.min(...picked.map(p => Math.hypot(p.x - n.x, p.z - n.z)));
            if (d > bd) { bd = d; best = n; }
        }
        if (!best || bd < 1.5) break;
        picked.push(best);
    }
    const b = m.bounds;
    const cx = b ? (b.min[0] + b.max[0]) / 2 : picked[0].x, cz = b ? (b.min[2] + b.max[2]) / 2 : picked[0].z;
    return picked.slice(1).map(n => ({
        position: [n.x, n.y + m.walk.eyeHeight, n.z],
        yaw: Math.atan2(-(cx - n.x), -(cz - n.z)) * 180 / Math.PI
    }));
}

function nearestRoom(rooms, pos) {
    let best = null, bd = 4;
    for (const r of rooms || []) {
        const d = Math.hypot(r.position[0] - pos[0], r.position[2] - pos[2]);
        if (d < bd) { bd = d; best = r; }
    }
    return best;
}

main().catch((e) => {
    console.error(e);
});
